import { BoldText, ShadowBox } from '@elements/Common';
import { DarkPopup } from '@elements/Popup';
import { CommonUtility } from '@utility/common';
import { ShipmentTypeKeys, tooltipTexts } from '@utility/constants/common';
import { cn, Tooltip } from '@skupreme/shared/ui';
import {
  BoatIcon,
  InfoIcon,
  PackageIcon,
  TruckIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import {
  cloneElement,
  forwardRef,
  type HTMLAttributes,
  isValidElement,
} from 'react';
import * as yup from 'yup';
import { DateUtility } from '@utility/date';
import type {
  ShipmentAttribute,
  ShipmentCreateItem,
  ShipmentCreateItems,
  ShipmentVariantType,
} from 'types/shipment-inbound/create';
import type {
  ShipmentBoxesType,
  ShipmentDetailItemType,
  ShipmentDetailType,
} from 'types/shipment-inbound/common';
import type { ShipmentDraftItemType } from 'types/shipment-inbound/draft';
import type {
  shipmentBoxInformationType,
  ShipmentDynamicFieldType,
} from '@hooks/shipment';
import type { IShipment } from 'types/shipment/types';

type DivProps = HTMLAttributes<HTMLDivElement>;

function block(displayName: string, classes: string) {
  const Block = forwardRef<HTMLDivElement, DivProps>(
    ({ className, ...rest }, ref) => (
      <div {...rest} ref={ref} className={cn(classes, className)} />
    ),
  );
  Block.displayName = displayName;
  return Block;
}

export const BoxHeader = block(
  'BoxHeader',
  'sk:flex sk:items-center sk:justify-between sk:p-[1em]',
);

type DisabledButtonProps = DivProps & {
  /** Accepted for old callers; these always fill their row. */
  fluid?: boolean;
};

/*
 * A div rather than a disabled button: these stand in for an action the user
 * lacks permission for, as the trigger of a tooltip saying so, and a disabled
 * button would swallow the hover that opens it.
 */
function disabledButton(displayName: string, classes: string) {
  const Disabled = forwardRef<HTMLDivElement, DisabledButtonProps>(
    ({ fluid: _fluid, className, ...rest }, ref) => (
      <div
        {...rest}
        ref={ref}
        className={cn(
          'sk:flex sk:h-[37px] sk:w-full sk:cursor-not-allowed sk:items-center sk:justify-center sk:rounded-option sk:text-surface sk:opacity-50',
          classes,
          className,
        )}
      />
    ),
  );
  Disabled.displayName = displayName;
  return Disabled;
}

export const DisabledDangerButton = disabledButton(
  'DisabledDangerButton',
  'sk:bg-danger',
);

export const DisabledStyledButton = disabledButton(
  'DisabledStyledButton',
  'sk:bg-primary',
);

export const Box = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <ShadowBox {...rest} ref={ref} className={cn('sk:p-0', className)} />
  ),
);
Box.displayName = 'Box';

export const BoxContainer = block('BoxContainer', 'sk:p-[1em]');

export const Row = block('Row', 'sk:mb-[1em]');

export const ShippingTypes = [
  {
    text: 'Full Container Load(s)',
    value: 'FCL',
    icon: <BoatIcon size={18} />,
  },
  {
    text: 'Less than Container Load',
    value: 'LCL',
    icon: <BoatIcon size={18} />,
  },
  {
    text: 'Less than / Full Truck Load(s)',
    value: 'LTL',
    icon: <TruckIcon size={18} />,
  },
  {
    text: 'Parcel(s)',
    value: 'SP',
    icon: <PackageIcon size={18} />,
  },
];

export const ShippingTypeIcons = {
  LCL: <BoatIcon size={18} />,
  FCL: <BoatIcon size={18} />,
  LTL: <TruckIcon size={18} />,
  SP: <PackageIcon size={18} />,
};

export const shipmentSchema = yup.object().shape({
  displayId: yup
    .string()
    .when('$editMode', (editMode, schema) =>
      editMode ? schema.required('Shipment ID is required') : schema.optional(),
    ),
  shipmentId: yup.string().nullable(),
  originType: yup.string().required(),
  originName: yup.string().optional().nullable(),
  destination: yup.string().when('originType', {
    is: val => val === ShipmentTypeKeys.WS,
    then: yup.string().optional().nullable(),
    otherwise: yup.string().required('Destination is required'),
  }),
  shipFrom: yup.string().when('originType', {
    is: val => val === ShipmentTypeKeys.WS,
    then: yup.string().required('Ship from is required'),
    otherwise: yup.string().optional().nullable(),
  }),
  fulfillmentProvider: yup.string().when('originType', {
    is: val => val === ShipmentTypeKeys.FP,
    then: yup.string().required(),
    otherwise: yup.string(),
  }),
  shippingType: yup.string(),
  memo: yup.string().nullable(),
  etd: yup.string(),
  eta: yup.string(),
  emailAddresses: yup.array().default([]),
  notify: yup.boolean(),
  qtyType: yup.string().default('Cartons'),
});

export const watchFormFields = [
  'destination',
  'originType',
  'origins',
  'fulfillmentProvider',
  'originName',
  'shipFrom',
  'shippingType',
];

export const allFormFields = [
  'displayId',
  'shipmentId',
  'originType',
  'destination',
  'origins',
  'fulfillmentProvider',
  'shippingType',
  'etd',
  'eta',
  'shipFrom',
];

export const DynamicFieldType = {
  array: 'array',
  button: 'button',
  checkbox: 'checkbox',
  checkboxButton: 'checkboxButton',
  currency: 'currency',
  currencyLabel: 'currencyLabel',
  date: 'date',
  dropdown: 'dropdown',
  file: 'file',
  flexArray: 'flexArray',
  hidden: 'hidden',
  icon: 'icon',
  label: 'label',
  number: 'numeric',
  popupBoxes: 'popupBoxes',
  popupPallet: 'popupPallet',
  radio: 'radio',
  radioCard: 'radioCard',
  radioDropdown: 'radioDropdown',
  radioArray: 'radioArray',
  text: 'text',
  timeline: 'timeline',
  tracking: 'tracking',
};

export const setDefaultDFValue = (
  items?: ShipmentDynamicFieldType[],
  value?: any,
) => {
  if (!value) {
    return {};
  }

  const temp = {};
  items?.forEach((item, index) => {
    switch (item.inputType) {
      case DynamicFieldType.checkboxButton:
        temp[item.name] = DynamicFieldType.checkboxButton;
        break;
      case DynamicFieldType.button:
        temp[item.name] = DynamicFieldType.button;
        break;
      case DynamicFieldType.label:
      case DynamicFieldType.currencyLabel:
      case DynamicFieldType.hidden:
        if (item.useDefault && CommonUtility.isNotEmpty(item.value)) {
          temp[item.name] = item.value || '';
        } else if (
          CommonUtility.isValidObject(value) &&
          CommonUtility.isNotEmpty(value[item.name])
        ) {
          temp[item.name] = value[item.name];
        } else {
          temp[item.name] = item.value || '';
        }
        break;

      case DynamicFieldType.radio:
      case DynamicFieldType.radioArray:
        if (CommonUtility.isNotEmpty(value[item.name])) {
          temp[item.name] = value[item.name];
        } else if (CommonUtility.isNotEmpty(item.value)) {
          temp[item.name] = item.value;
        } else if (CommonUtility.isNotEmpty(item.default)) {
          temp[item.name] = item.default;
        } else {
          temp[item.name] = item.options?.[0].value;
        }
        break;

      case DynamicFieldType.checkbox:
        temp[item.name] = value[item.name] || false;
        break;

      case DynamicFieldType.currency:
      case DynamicFieldType.number: {
        const rawValue = Array.isArray(value)
          ? (value[index]?.value ?? value[index]?.default)
          : value[item.name];

        if (rawValue === undefined || rawValue === null || rawValue === '') {
          temp[item.name] = '';
          break;
        }

        let numericValue = Number(rawValue);

        if (isNaN(numericValue)) {
          temp[item.name] = '';
          break;
        }

        if (
          item.maskOptions?.maxValue !== undefined &&
          numericValue > item.maskOptions.maxValue
        ) {
          numericValue = item.maskOptions.maxValue;
        }

        temp[item.name] = numericValue;
        break;
      }
      case DynamicFieldType.array:
      case DynamicFieldType.flexArray:
        //@ts-expect-error Argument of type 'string | undefined' is not assignable to parameter of type 'unknown[]
        if (CommonUtility.isValidArray(item.default)) {
          temp[item.name] = item.default?.map(array =>
            setDefaultDFValue(item.nestedFields, array),
          );
        } else if (CommonUtility.isValidArray(value[item.name])) {
          temp[item.name] = value[item.name].map(array =>
            setDefaultDFValue(item.nestedFields, array),
          );
        } else {
          temp[item.name] = [
            setDefaultDFValue(item.nestedFields, item.nestedFields),
          ];
        }
        break;

      case DynamicFieldType.date:
        if (value[item.name]) {
          temp[item.name] = new Date(value[item.name]);
        } else {
          temp[item.name] = '';
        }
        break;

      default:
        temp[item.name] = value[item.name] || item.value || '';
        if (
          CommonUtility.isValidObject(value[item.name]) &&
          value[item.name]?.carrierCodeValue
        ) {
          temp[item.name] = value[item.name]?.carrierCodeValue;
        }

        break;
    }
  });
  return temp;
};

export const setDFError = (fields: ShipmentDynamicFieldType[] = [], value) => {
  if (Object.keys(value || {}).length === 0) {
    return {};
  }
  const error = {};
  for (const item of fields) {
    if (item.isHidden || item.hideField) {
      continue;
    }

    switch (item.inputType) {
      case DynamicFieldType.popupBoxes:
        (value[item.name] || []).forEach(box => {
          if (
            !box.dimensions?.length ||
            !box.dimensions?.width ||
            !box.dimensions?.height ||
            !box.weight?.value
          ) {
            error[item.name] = 'All box fields are required';
          }
        });
        break;
      case DynamicFieldType.array:
      case DynamicFieldType.flexArray:
        error[item.name] = [];
        (value[item.name] || []).forEach(row => {
          error[item.name].push(setDFError(item.nestedFields, row || {}));
        });
        break;

      case DynamicFieldType.button:
      case DynamicFieldType.file:
      case DynamicFieldType.checkbox:
      case DynamicFieldType.checkboxButton:
      case DynamicFieldType.hidden:
      case DynamicFieldType.label:
      case DynamicFieldType.currencyLabel:
      case DynamicFieldType.radio:
        error[item.name] = '';
        break;

      default:
        if (item.required) {
          error[item.name] =
            value && value[item.name] ? '' : `${item.name} is required`;
        } else {
          error[item.name] = '';
        }
        break;
    }
  }
  return error;
};

const keys = {
  carton_length: 'carton_length',
  carton_width: 'carton_width',
  carton_height: 'carton_height',
  carton_net_weight: 'carton_net_weight',
  carton_gross_weight: 'carton_gross_weight',
};

export const convertItemToMetric = (attribute, toFixed = true) => {
  for (const key in keys) {
    if (Object.prototype.hasOwnProperty.call(keys, key)) {
      if (!attribute || attribute === null || attribute === undefined) break;

      if (['inch', 'in'].includes(attribute[key]?.unit)) {
        const value = CommonUtility.inchToCenti(
          (attribute && attribute[key] && attribute[key].value) || 0,
          // @ts-expect-error - TS2554: Expected 1 arguments, but got 2.
          toFixed,
        );
        if (attribute) {
          attribute[key] = { unit: 'cm', value };
        } else {
          attribute = { [key]: { unit: 'cm', value } };
        }
      } else if (attribute[key]?.unit === 'lb') {
        const value = CommonUtility.poundToKilo(
          (attribute && attribute[key] && attribute[key].value) || 0,
          // @ts-expect-error - TS2554: Expected 1 arguments, but got 2.
          toFixed,
        );
        if (attribute) {
          attribute[key] = { unit: 'kg', value };
        } else {
          attribute = { [key]: { unit: 'kg', value } };
        }
      }
    }
  }
  return attribute;
};

export const convertItemToImperial = (attribute, toFixed = true) => {
  for (const key in keys) {
    if (Object.prototype.hasOwnProperty.call(keys, key)) {
      if (!attribute || attribute === null || attribute === undefined) break;

      if (attribute[key]?.unit === 'cm') {
        const value = CommonUtility.centiToInch(
          (attribute && attribute[key] && attribute[key]?.value) || 0,
          // @ts-expect-error - TS2554: Expected 1 arguments, but got 2.
          toFixed,
        );
        if (attribute) {
          attribute[key] = { unit: 'inch', value };
        } else {
          attribute = { [key]: { unit: 'inch', value } };
        }
      } else if (attribute[key]?.unit === 'kg') {
        const value = CommonUtility.kiloToPound(
          (attribute && attribute[key] && attribute[key]?.value) || 0,
          // @ts-expect-error - TS2554: Expected 1 arguments, but got 2.
          toFixed,
        );
        if (attribute) {
          attribute[key] = { unit: 'lb', value };
        } else {
          attribute = { [key]: { unit: 'lb', value } };
        }
      }
    }
  }
  return attribute;
};

export const getLatestAttribute = (item, type, createdShipment = true) => {
  let latestAttribute = null;

  if (!CommonUtility.isValidObject(item)) return {};

  if ([ShipmentTypeKeys.WS, ShipmentTypeKeys.PO].includes(type as any)) {
    if (CommonUtility.isValidArray(item.aliases)) {
      latestAttribute =
        item.aliases?.find(x => CommonUtility.isValidObject(x.attributes))
          ?.attributes || {};

      if (latestAttribute) {
        // @ts-expect-error - TS2339: Property 'alias' does not exist on type 'never'.
        latestAttribute.alias =
          // @ts-expect-error - TS2339: Property 'alias' does not exist on type 'never'.
          latestAttribute.alias || item.aliases?.[0]?.alias;
        // @ts-expect-error - TS2339: Property 'carton_qty' does not exist on type 'never'.
        latestAttribute.carton_qty =
          // @ts-expect-error - TS2339: Property 'carton_qty' does not exist on type 'never'.
          latestAttribute.carton_qty || item.aliases?.[0]?.carton_qty;
        if (
          // @ts-expect-error - TS2339: Property 'carton_qty' does not exist on type 'never'.
          !CommonUtility.isNotNull(latestAttribute.carton_qty) &&
          // @ts-expect-error - TS2339: Property 'units_per_carton' does not exist on type 'never'.
          latestAttribute.units_per_carton
        ) {
          const calcCartons =
            // @ts-expect-error - TS2339: Property 'units_per_carton' does not exist on type 'never'.
            item.aliases?.[0]?.qty / latestAttribute.units_per_carton;
          // @ts-expect-error - TS2339: Property 'carton_qty' does not exist on type 'never'.
          latestAttribute.carton_qty = calcCartons;
        }
      }
    } else {
      latestAttribute =
        (item.attributes || []).length > 0
          ? item.attributes.find(x => x.selected) ||
            item.attributes.find(
              x => item.defaultAlias === x.alias && x.units_per_carton,
            ) ||
            item.attributes.find(x => x.primary && x.units_per_carton) ||
            item.attributes.find(x => x.units_per_carton) ||
            item.attributes[item.attributes.length - 1]
          : createdShipment
            ? item.attribute || {}
            : {};
    }
  } else {
    latestAttribute =
      item.variants?.length > 0
        ? item.variants.find(x => x.selected)?.attribute ||
          item.variants.find(
            x =>
              x.attribute &&
              Object.keys(x.attribute).length > 1 &&
              x.attribute.available_qty !== 0,
          )?.attribute ||
          {}
        : {};
  }

  return latestAttribute;
};

type ShipmentLatestAttributeReturnType = ShipmentAttribute;

export const getShipmentLatestAttribute = (
  item: ShipmentCreateItem,
  type: (typeof ShipmentTypeKeys)[keyof typeof ShipmentTypeKeys],
  createdShipment = true,
): ShipmentLatestAttributeReturnType => {
  let latestAttribute: ShipmentLatestAttributeReturnType =
    {} as ShipmentLatestAttributeReturnType;

  if (!CommonUtility.isValidObject(item))
    return {} as ShipmentLatestAttributeReturnType;

  if ([ShipmentTypeKeys.WS, ShipmentTypeKeys.PO].includes(type as any)) {
    if (CommonUtility.isValidArray(item.aliases)) {
      latestAttribute =
        item.aliases?.find(x => CommonUtility.isValidObject(x.attributes))
          ?.attributes || ({} as ShipmentAttribute);

      if (latestAttribute) {
        latestAttribute.alias =
          latestAttribute.alias || item.aliases?.[0]?.alias;
        latestAttribute.cartonQty =
          latestAttribute.cartonQty || item.aliases?.[0]?.cartonQty;
        if (
          !CommonUtility.isNotNull(latestAttribute.cartonQty) &&
          latestAttribute.unitsPerCarton
        ) {
          const calcCartons =
            item.aliases?.[0]?.availableQty / latestAttribute.unitsPerCarton;
          latestAttribute.cartonQty = calcCartons;
        }
      }
    } else {
      // @ts-expect-error - TS2322: Type '{} | ShipmentAttribute' is not assignable to type 'ShipmentAttribute'.
      latestAttribute =
        (item.attributes || []).length > 0
          ? item.attributes.find(x => x.selected) ||
            item.attributes.find(
              x => item.defaultAlias === x.alias && x.unitsPerCarton,
            ) ||
            item.attributes.find(x => x.primary && x.unitsPerCarton) ||
            item.attributes.find(x => x.unitsPerCarton) ||
            item.attributes[item.attributes.length - 1]
          : createdShipment
            ? item.attribute || {}
            : {};
    }
  } else {
    // @ts-expect-error - TS2322: Type '{} | ShipmentAttribute' is not assignable to type 'ShipmentAttribute'.
    latestAttribute =
      item.variants?.length > 0
        ? item.variants.find(x => x.selected)?.attribute ||
          item.variants.find(
            x => CommonUtility.isValidObject(x.attribute) && x.available !== 0,
          )?.attribute ||
          {}
        : {};
  }

  return latestAttribute;
};

export const validateAttribute = attribute => {
  for (const key in keys) {
    if (!attribute?.[key]?.value) {
      return false;
    }
  }
  if (!attribute.alias || !attribute.units_per_carton) return false;

  return true;
};

const ShipmentAttributeKeys = {
  cartonLength: 'cartonLength',
  cartonWidth: 'cartonWidth',
  cartonHeight: 'cartonHeight',
  cartonNetWeight: 'cartonNetWeight',
  cartonGrossWeight: 'cartonGrossWeight',
};

type ShipmentAttributeType = {
  cartonLength?: { unit: string; value?: number };
  cartonWidth?: { unit: string; value?: number };
  cartonHeight?: { unit: string; value?: number };
  cartonNetWeight?: { unit: string; value?: number };
  cartonGrossWeight?: { unit: string; value?: number };
  alias?: string;
  unitsPerCarton?: number;
  [key: string]: any;
};

type validateShipmentAttributeReturnType = {
  isValid: boolean;
  errorMessage?: string;
};

const validateShipmentAttributeMessages = {
  invalidCarton: 'Invalid Carton Dimensions or Weight',
  invalidAlias: 'Alias is required',
  invalidUnitsPerCarton: 'Invalid Units Per Carton',
  fbaError: 'Dimensions cannot exceed 25in X 25in X 36 in',
};

export const validateShipmentAttribute = (
  attribute: ShipmentAttributeType,
  isFBA = false,
): validateShipmentAttributeReturnType => {
  for (const key in ShipmentAttributeKeys) {
    if (!attribute?.[key]?.value) {
      return {
        isValid: false,
        errorMessage: validateShipmentAttributeMessages.invalidCarton,
      };
    }
  }

  if (!attribute.alias || !attribute.unitsPerCarton) {
    return {
      isValid: false,
      errorMessage: !attribute.alias
        ? validateShipmentAttributeMessages.invalidAlias
        : validateShipmentAttributeMessages.invalidUnitsPerCarton,
    };
  }

  if (isFBA) {
    let dimensions = [
      attribute?.cartonLength?.value || 0,
      attribute?.cartonWidth?.value || 0,
      attribute?.cartonHeight?.value || 0,
    ];

    if (attribute.cartonLength?.unit === 'in') {
      dimensions = dimensions.map(dim => CommonUtility.inchToCenti(dim));
    }

    dimensions = dimensions.sort((a, b) => a - b); // asc

    if (dimensions[0] > 63.5 || dimensions[1] > 63.5 || dimensions[2] > 91.44) {
      return {
        isValid: false,
        errorMessage: validateShipmentAttributeMessages.fbaError,
      };
    }
  }

  return {
    isValid: true,
  };
};

export const calculateCBM = (items, userValue, type) => {
  let tempCBM = 0;

  const isPO = [ShipmentTypeKeys.PO, ShipmentTypeKeys.WS].includes(type);
  const attributeKey = isPO ? 'attributes' : 'attribute';
  const aliasesKey = isPO ? 'aliases' : 'variants';

  if (CommonUtility.isValidArray(items)) {
    items.forEach(temp => {
      // @ts-expect-error - TS18046: 'temp' is of type 'unknown'.
      const upcQty = userValue[temp._id];
      if (upcQty) {
        // @ts-expect-error - TS18046: 'temp' is of type 'unknown'.
        temp.items.forEach(item => {
          if (upcQty[item._id]) {
            let latestAttribute = {};
            const isMultiple = isPO
              ? item.aliases?.length > 1
              : item.variants?.filter(x => x.attribute)?.length > 1;
            if (isMultiple) {
              item[aliasesKey]?.forEach(variant => {
                const ctns = Number(upcQty[item._id]?.[variant.alias]);
                latestAttribute = variant[attributeKey];
                let calc = 0;

                if (ctns) {
                  // @ts-expect-error - TS2339: Property 'carton_length' does not exist on type '{}'.
                  if (latestAttribute.carton_length?.unit === 'inch') {
                    calc = CommonUtility.cbf(latestAttribute, ctns);
                  } else {
                    calc = CommonUtility.cbm(latestAttribute, ctns);
                  }
                }
                tempCBM += calc;
              });
            } else if (upcQty[item._id].ctn_qty) {
              // @ts-expect-error - TS2322: Type '{} | null' is not assignable to type '{}'.
              latestAttribute = getLatestAttribute(item, type);
              let calc = 0;
              // @ts-expect-error - TS2339: Property 'carton_length' does not exist on type '{}'.
              if (latestAttribute.carton_length?.unit === 'inch') {
                calc = CommonUtility.cbf(
                  latestAttribute,
                  upcQty[item._id].ctn_qty,
                );
              } else {
                calc = CommonUtility.cbm(
                  latestAttribute,
                  upcQty[item._id].ctn_qty,
                );
              }
              tempCBM += calc;
            }
          }
        });
      }
    });
  }
  return tempCBM;
};

export const calculateCBMConverted = (
  userValue: ShipmentUserValueType,
  type: string,
  additionalItems: ShipmentDetailItemType[] = [],
  user_item_key = 'item_id',
) => {
  let tempCBM = 0;

  const isPO = [ShipmentTypeKeys.PO, ShipmentTypeKeys.WS].includes(type as any);
  const attributeKey = isPO ? 'attributes' : 'attribute';
  const aliasesKey = isPO ? 'aliases' : 'variants';

  Object.keys(userValue).forEach(orgKey => {
    const upcQty = userValue[orgKey];
    Object.keys(userValue[orgKey]).forEach(itemKey => {
      const item = userValue?.[orgKey]?.[itemKey]?.orgItem || {};
      if (upcQty[item._id]) {
        let latestAttribute = {} as any;
        const isMultiple = isPO
          ? item.aliases?.length > 1
          : item.variants?.filter(x => x.attribute)?.length > 1;
        if (isMultiple) {
          item[aliasesKey]?.forEach(variant => {
            const ctns = Number(upcQty[item._id]?.[variant.alias]);
            latestAttribute = variant[attributeKey];
            const calc = CommonUtility.cbm(
              {
                carton_height: latestAttribute.cartonHeight,
                carton_length: latestAttribute.cartonLength,
                carton_width: latestAttribute.cartonWidth,
              },
              ctns,
            );
            tempCBM += calc;
          });
        } else if (upcQty[item._id].ctn_qty) {
          latestAttribute = getShipmentLatestAttribute(item, type as any);
          const calc = CommonUtility.cbm(
            latestAttribute,
            upcQty[item._id].ctnQty,
          );
          tempCBM += calc;
        }
      }
    });
  });

  additionalItems?.forEach(item => {
    if (!userValue[item.originId]?.[item[user_item_key]]) {
      // Go further only if items are left

      item.aliases?.forEach(variant => {
        const ctns = Math.round(
          variant.qty / variant.attributes.unitsPerCarton,
        );
        let calc = 0;

        if (ctns) {
          calc = CommonUtility.cbm(
            {
              carton_height: variant.attributes.cartonHeight,
              carton_length: variant.attributes.cartonLength,
              carton_width: variant.attributes.cartonWidth,
            },
            ctns,
          );
        }
        tempCBM += calc;
      });
    }
  });
  return tempCBM;
};

export const calculateTotalWeight = (items, userValue) => {
  let totalWeight = 0;
  items.forEach(temp => {
    const upcQty = userValue[temp._id];
    temp.items.forEach(item => {
      if (upcQty && upcQty[item._id] && upcQty[item._id].ctn_qty) {
        const latestAttribute =
          (item.attributes || []).length > 0
            ? item.attributes.find(x => x.primary) ||
              item.attributes[item.attributes.length - 1]
            : null;
        totalWeight += latestAttribute.carton_gross_weight?.value;
      }
    });
  });
  return totalWeight;
};

export const calculateTotal = (userValue, items = [], type) => {
  const total = {
    cartons: 0,
    units: 0,
    retails: 0,
    supplierValue: 0,
    grossWeight: 0,
  };

  const isPO = [ShipmentTypeKeys.PO, ShipmentTypeKeys.WS].includes(type);
  const attributeKey = isPO ? 'attributes' : 'attribute';
  const aliasesKey = isPO ? 'aliases' : 'variants';
  if (CommonUtility.isValidArray(items)) {
    items.forEach(temp => {
      // @ts-expect-error - TS2339: Property '_id' does not exist on type 'never'.
      const upcQty = userValue[temp._id];
      // @ts-expect-error - TS2339: Property 'items' does not exist on type 'never'.
      temp.items.forEach(item => {
        if (upcQty && upcQty[item._id]) {
          const isMultiple = isPO
            ? item.aliases?.length > 1
            : item.variants?.filter(x => x.attribute)?.length > 1;
          if (isMultiple) {
            let units = 0;
            item[aliasesKey]?.forEach(variant => {
              const ctns = Number(upcQty[item._id]?.[variant.alias]);
              if (ctns) {
                total.cartons += ctns;
                total.grossWeight +=
                  ctns *
                  (variant[attributeKey]?.carton_gross_weight?.value || 0);
                units += ctns * variant[attributeKey]?.units_per_carton;
              }
            });
            total.units += units;
            total.retails += units * (upcQty[item._id].price || 0);
            total.supplierValue +=
              units * (upcQty[item._id].supplierItemCost || 0);
          } else if (upcQty[item._id].ctn_qty) {
            const userItem = upcQty[item._id];
            total.cartons += +userItem.ctn_qty || 0;
            total.grossWeight +=
              (+userItem.ctn_qty || 0) * (userItem.grossWeight || 0);
            total.units += +userItem.qty || 0;
            total.retails += (+userItem.qty || 0) * (userItem.price || 0);
            total.supplierValue +=
              (+userItem.qty || 0) * (userItem.supplierItemCost || 0);
          }
        }
      });
    });
  }

  return total;
};

export const calculateTotalConverted = (
  userValue: ShipmentUserValueType,
  type: string,
  additionalItems: ShipmentDetailItemType[] = [],
  user_item_key = 'itemId',
  isUnitView = false,
) => {
  const total = {
    cartons: 0,
    units: 0,
    retails: 0,
    supplierValue: 0,
    grossWeight: 0,
  };

  const isPO = [ShipmentTypeKeys.PO, ShipmentTypeKeys.WS].includes(type as any);
  const attributeKey = isPO ? 'attributes' : 'attribute';
  const aliasesKey = isPO ? 'aliases' : 'variants';
  Object.keys(userValue).forEach(orgKey => {
    const upcQty = userValue[orgKey];
    Object.keys(userValue[orgKey]).forEach(itemKey => {
      const item = userValue?.[orgKey]?.[itemKey]?.orgItem || {};
      if (upcQty && upcQty[item._id]) {
        const isMultiple = isPO
          ? item.aliases?.length > 1
          : item.variants?.filter(x => x.attribute)?.length > 1;

        if (isMultiple && !isUnitView) {
          let units = 0;
          item[aliasesKey]?.forEach(variant => {
            const ctns = Number(upcQty[item._id]?.[variant.alias]);
            if (ctns) {
              total.cartons += ctns;
              total.grossWeight +=
                ctns * (variant[attributeKey]?.cartonGrossWeight?.value || 0);
              units += ctns * variant[attributeKey]?.unitPerCarton;
            }
          });
          total.units += units;
          total.retails += units * (upcQty[item._id].price || 0);
          total.supplierValue +=
            units * (upcQty[item._id].supplierItemCost || 0);
        } else if (upcQty[item._id].ctnQty || isUnitView) {
          const userItem = upcQty[item._id];
          total.cartons += +userItem.ctnQty || 0;
          total.grossWeight +=
            (+userItem.ctnQty || 0) * (userItem.grossWeight || 0);
          total.units += +userItem.qty || 0;
          total.retails += (+userItem.qty || 0) * (userItem.price || 0);
          total.supplierValue +=
            (+userItem.qty || 0) * (userItem.supplierItemCost || 0);
        }
      }
    });
  });

  additionalItems?.forEach(item => {
    if (!userValue[item.originId]?.[item[user_item_key]]) {
      // Go further only if items are left
      let units = 0;
      item.aliases?.forEach(variant => {
        if (variant.attributes.unitsPerCarton) {
          const ctns = Math.round(
            variant.qty / variant.attributes.unitsPerCarton,
          );
          if (ctns) {
            total.cartons += ctns;
            total.grossWeight +=
              ctns * (variant.attributes?.cartonGrossWeight?.value || 0);
            units += variant.qty;
          }
        }
      });
      total.units += units;
      total.retails += units * (item.price || 0);
      total.supplierValue += units * (item.supplierItemCost || 0);
    }
  });
  return total;
};

export const calculatePublicCBM = items => {
  let tempCBM = 0;
  items?.forEach(item => {
    if (item.qty) {
      let latestAttribute = item.attributes || {};
      const isMultiple = CommonUtility.isValidArray(item.aliases);
      if (isMultiple) {
        item.aliases.forEach(aliasItem => {
          latestAttribute = aliasItem.attributes;
          const ctns = aliasItem.qty / latestAttribute.units_per_carton;
          let calc = 0;
          if (ctns) {
            if (latestAttribute.carton_length?.unit === 'inch') {
              calc = CommonUtility.cbf(latestAttribute, ctns);
            } else {
              calc = CommonUtility.cbm(latestAttribute, ctns);
            }
          }
          tempCBM += calc;
        });
      } else {
        let calc = 0;
        if (latestAttribute.carton_length.unit === 'inch') {
          calc = CommonUtility.cbf(
            latestAttribute,
            item.qty / (latestAttribute?.units_per_carton || 0),
          );
        } else {
          calc = CommonUtility.cbm(
            latestAttribute,
            item.qty / (latestAttribute?.units_per_carton || 0),
          );
        }
        tempCBM += calc;
      }
    }
  });
  return tempCBM || 0;
};

export const calculateShipmentPublicCBM = (items: ShipmentDetailItemType[]) => {
  let tempCBM = 0;
  items?.forEach(item => {
    if (item.qty) {
      let latestAttribute = item.aliases[0]?.attributes || {};
      const isMultiple = CommonUtility.isValidArray(item.aliases);
      if (isMultiple) {
        item.aliases.forEach(aliasItem => {
          latestAttribute = aliasItem.attributes;
          const ctns = aliasItem.qty / latestAttribute.unitsPerCarton;
          let calc = 0;
          if (ctns) {
            if (latestAttribute.cartonLength?.unit === 'inch') {
              calc = CommonUtility.cbf(
                {
                  carton_height: latestAttribute.cartonHeight,
                  carton_length: latestAttribute.cartonLength,
                  carton_width: latestAttribute.cartonWidth,
                },
                ctns,
              );
            } else {
              calc = CommonUtility.cbm(
                {
                  carton_height: latestAttribute.cartonHeight,
                  carton_length: latestAttribute.cartonLength,
                  carton_width: latestAttribute.cartonWidth,
                },
                ctns,
              );
            }
          }
          tempCBM += calc;
        });
      } else {
        let calc = 0;
        if (latestAttribute.cartonLength.unit === 'inch') {
          calc = CommonUtility.cbf(
            {
              carton_height: latestAttribute.cartonHeight,
              carton_length: latestAttribute.cartonLength,
              carton_width: latestAttribute.cartonWidth,
            },
            item.qty / (latestAttribute?.unitsPerCarton || 0),
          );
        } else {
          calc = CommonUtility.cbm(
            {
              carton_height: latestAttribute.cartonHeight,
              carton_length: latestAttribute.cartonLength,
              carton_width: latestAttribute.cartonWidth,
            },
            item.qty / (latestAttribute?.unitsPerCarton || 0),
          );
        }
        tempCBM += calc;
      }
    }
  });
  return tempCBM || 0;
};

export const searchCompare = (query, compareTo) =>
  compareTo?.toLowerCase()?.includes(query?.toLowerCase());

export const calculatePublicTotal = (items: ShipmentDetailItemType[]) => {
  const total = {
    cartons: 0,
    units: 0,
    retails: 0,
    supplierValue: 0,
    grossWeight: 0,
  };
  items?.forEach(item => {
    let latestAttribute = item.aliases?.[0]?.attributes;
    const isMultiple = CommonUtility.isValidArray(item.aliases);

    if (isMultiple) {
      let perItemUnits = 0;
      item.aliases.forEach(aliasItem => {
        latestAttribute = aliasItem.attributes;
        const units = aliasItem.qty;
        const ctns = aliasItem.qty / latestAttribute.unitsPerCarton;

        if (ctns) {
          total.cartons += ctns;
          total.units += +units || 0;
          perItemUnits += +units || 0;
          total.grossWeight += ctns * latestAttribute.cartonGrossWeight.value;
        }
      });
      total.retails += (perItemUnits || 0) * (item.price || 0);
      total.supplierValue += (perItemUnits || 0) * (item.supplierItemCost || 0);
    } else if (item.qty && latestAttribute) {
      const cartons = (item.qty || 0) / (latestAttribute.unitsPerCarton || 1);
      total.cartons += cartons;
      total.units += +item.qty || 0;
      total.retails += (+item.qty || 0) * (item.price || 0);
      total.supplierValue += (+item.qty || 0) * (item.supplierItemCost || 0);
      total.grossWeight += cartons * latestAttribute.cartonGrossWeight?.value;
    }
  });
  return total;
};

export const calculateListTotal = (items, isUnitView = false) => {
  const total = {
    cartons: 0,
    units: 0,
    retails: 0,
    supplierValue: 0,
    grossWeight: 0,
  };

  items.forEach(item => {
    let latestAttribute = item.attributes?.find?.(x =>
      CommonUtility.isValidObject(x.attributes),
    )?.attributes;
    const isMultiple = CommonUtility.isValidArray(item.aliases);

    if (isMultiple && !isUnitView) {
      let cartons = 0;
      let unitsTotal = 0;
      item.aliases.forEach(aliasItem => {
        latestAttribute = aliasItem.attributes;
        const units = aliasItem.qty;
        const ctns = aliasItem.qty / latestAttribute.units_per_carton;

        if (ctns) {
          cartons += ctns;
          total.cartons += ctns;
          total.units += +units || 0;
          unitsTotal += +units || 0;
          total.grossWeight += ctns * latestAttribute.carton_gross_weight.value;
        }
      });
      total.retails += (cartons || 0) * (item.price || 0);
      total.supplierValue +=
        (unitsTotal || 0) *
        (item.supplierItemCost || item.supplierComponentCost || 0);
    } else if (item.qty) {
      const cartons =
        (item.qty || 0) / (latestAttribute?.units_per_carton || 1);
      total.cartons += cartons;
      total.units += +item.qty || 0;
      total.retails += (+item.qty || 0) * (item.price || 0);
      total.supplierValue +=
        (+item.qty || 0) *
        (item.supplierItemCost || item.supplierComponentCost || 0);
      total.grossWeight +=
        cartons * latestAttribute?.carton_gross_weight?.value;
    }
  });
  return total;
};

export const calculateListTotalV2 = (
  items: IShipment['items'],
  isUnitView = false,
) => {
  const total = {
    cartons: 0,
    units: 0,
    retails: 0,
    supplierValue: 0,
    grossWeight: 0,
  };

  items.forEach(item => {
    let latestAttribute = item.aliases?.find?.(x =>
      CommonUtility.isValidObject(x.attributes),
    )?.attributes;
    const isMultiple = CommonUtility.isValidArray(item.aliases);

    if (isMultiple && !isUnitView) {
      let cartons = 0;
      let unitsTotal = 0;
      item.aliases.forEach(aliasItem => {
        latestAttribute = aliasItem.attributes;
        const units = aliasItem.qty;
        const ctns = aliasItem.qty / latestAttribute.unitsPerCarton;

        if (ctns) {
          cartons += ctns;
          total.cartons += ctns;
          total.units += +units || 0;
          unitsTotal += +units || 0;
          total.grossWeight += ctns * latestAttribute.cartonGrossWeight.value;
        }
      });
      total.retails += (cartons || 0) * (item.price || 0);
      total.supplierValue += (unitsTotal || 0) * (item.supplierItemCost || 0);
    } else if (item.qty) {
      const cartons = (item.qty || 0) / (latestAttribute?.unitsPerCarton || 1);
      total.cartons += cartons;
      total.units += +item.qty || 0;
      total.retails += (+item.qty || 0) * (item.price || 0);
      total.supplierValue += (+item.qty || 0) * (item.supplierItemCost || 0);
      total.grossWeight +=
        cartons * (latestAttribute?.cartonGrossWeight?.value || 0);
    }
  });
  return total;
};

export const shipmentCalculateListTotal = (
  items?: ShipmentDraftItemType[],
  isUnitView = false,
) => {
  const total = {
    cartons: 0,
    units: 0,
    retails: 0,
    supplierValue: 0,
    grossWeight: 0,
  };

  if (!CommonUtility.isValidArray(items)) {
    return total;
  }

  items.forEach(item => {
    let latestAttribute = item.aliases?.find?.(x =>
      CommonUtility.isValidObject(x.attributes),
    )?.attributes;

    const isMultiple = CommonUtility.isValidArray(item.aliases);

    if (isMultiple && !isUnitView) {
      let cartons = 0;
      let unitsTotal = 0;
      item.aliases.forEach(aliasItem => {
        latestAttribute = aliasItem.attributes;
        const units = aliasItem.qty;
        const ctns = aliasItem.qty / latestAttribute.unitsPerCarton;

        if (ctns) {
          cartons += ctns;
          total.cartons += ctns;
          total.units += +units || 0;
          unitsTotal += +units || 0;
          total.grossWeight += ctns * latestAttribute?.cartonGrossWeight?.value;
        }
      });
      total.retails += (cartons || 0) * (item.price || 0);
      total.supplierValue += (unitsTotal || 0) * (item.supplierItemCost || 0);
    } else if (item.qty) {
      const cartons = (item.qty || 0) / (latestAttribute?.unitsPerCarton || 1);
      total.cartons += cartons;
      total.units += +item.qty || 0;
      total.retails += (+item.qty || 0) * (item.price || 0);
      total.supplierValue += (+item.qty || 0) * (item.supplierItemCost || 0);
      total.grossWeight +=
        cartons * (latestAttribute?.cartonGrossWeight?.value || 0);
    }
  });
  return total;
};

export const hasDynamicFieldValid = fields => {
  let valid = true;

  const keys = Object.keys(fields);
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    const value = fields[key];
    if (typeof value === 'string') {
      if (value) {
        return false;
      }
    } else {
      for (let i = 0; i < value.length; i += 1) {
        const element = value[i];
        valid = hasDynamicFieldValid(element);
        if (!valid) {
          return false;
        }
      }
    }
  }
  return valid;
};

export const LabelOptions = [
  {
    value: 'SELLER_LABEL',
    text: 'Merchant',
  },
  {
    value: 'AMAZON_LABEL_ONLY',
    text: 'Amazon',
  },
];

export const paperOptions = [
  {
    value: 'Plain Paper',
    text: 'Plain Paper',
  },
  {
    value: 'Blank Rectangle Label',
    text: 'Blank Rectangle Label',
  },
  {
    value: 'Thermal Printing Paper',
    text: 'Thermal Printing Paper',
  },
];

function CBMPopupText({ imperial, cbm, cbf }) {
  return (
    <div>
      <div>{imperial ? tooltipTexts.cbf : tooltipTexts.cbm}</div>
      <br />
      {imperial ? (
        <div>
          CBM ={' '}
          {CommonUtility.decimalWithCommas(CommonUtility.roundNumber(cbm))}
        </div>
      ) : (
        <div>
          CBF ={' '}
          {CommonUtility.decimalWithCommas(CommonUtility.roundNumber(cbf))}
        </div>
      )}
    </div>
  );
}

export function CBMRenderer({ cbm, cbf, imperial, className, dark = false }) {
  return (
    <div className={cn('sk:flex! sk:items-center!', className)}>
      <Tooltip
        content={<CBMPopupText imperial={imperial} cbm={cbm} cbf={cbf} />}
      >
        <div className="sk:flex sk:items-center sk:text-fg-tertiary">
          <span>{imperial ? 'CBF ' : 'CBM '}</span>
          <BoldText
            className={cn(
              'sk:mx-1! value',
              dark ? 'sk:text-primary' : 'sk:text-fg-tertiary',
            )}
          >
            {CommonUtility.roundNumber(imperial ? cbf : cbm)}
          </BoldText>
          <InfoIcon />
        </div>
      </Tooltip>
    </div>
  );
}

export const summaryKeys = [
  'cartons',
  'units',
  'retails',
  'supplierValue',
  'grossWeight',
];

export const shipmentUnitTypes = [
  {
    text: 'Seller',
    value: 'SELLER',
    key: 'SELLER',
  },
  {
    text: 'None',
    value: 'NONE',
    key: 'NONE',
  },
];

export const AWDshipmentUnitTypes = [
  {
    text: 'Amazon',
    value: 'AMAZON',
    key: 'AMAZON',
  },
  {
    text: 'Self',
    value: 'SELF',
    key: 'SELF',
  },
];

export function ShipmentError({
  shipment,
  iconProps = {},
  trigger = null,
  fallback = null,
}) {
  if (
    !shipment?.shipment_error_alert ||
    !CommonUtility.isValidArray(shipment?.shipment_error_alert_messages)
  )
    return isValidElement(fallback) ? cloneElement(fallback) : <></>;

  return (
    <DarkPopup
      hoverable
      trigger={
        isValidElement(trigger) ? (
          cloneElement(trigger)
        ) : (
          <WarningIcon className="sk:text-danger" {...iconProps} />
        )
      }
    >
      <div className="sk:w-[350px] sk:max-w-fit">
        {shipment?.shipment_error_alert_messages?.map(
          (error_message, index: number) => (
            <div
              key={index}
              className="sk:mb-1!"
              dangerouslySetInnerHTML={{ __html: error_message?.text }}
            />
          ),
        )}
      </div>
    </DarkPopup>
  );
}

export const AWDDefaultValues = {
  SELF: 'SELF',
  SELLER: 'SELF',
  AMAZON: 'AMAZON',
  NONE: '',
};

export type ShipmentItemUserValue = {
  grossWeight?: number;
  originId: string;
  itemId: string;
  alias?: string;
  orgItem: ShipmentCreateItem;
  supplierItemCost?: number;
  sku?: string;
  price?: number;
  asin?: string | null;
  qty: number;
  expiration_date: Date | string;
  expanded: boolean;
  QuantityInCase: number;
  ctnQty: number;
  // this is fallback Qty when ctnQty is not valid
  oldQty?: number;
  labelOwner?: string;
  prepOwner?: string;

  // dynamic keys:
  // - alias based quantities (e.g. WGT3045)
  // - box based quantities (e.g. box-123)
  [dynamicKey: string]: any;
};

export type ShipmentUserValueType = {
  [originId: string]: {
    [itemId: string]: ShipmentItemUserValue;
  };
};

export const getDBItemUserValue = (
  items: ShipmentCreateItems | originItemsType,
  shipment?: ShipmentDetailType,
  isUnitView = false,
  isPOShipment?: boolean,
  destinationType?: string,
  allBoxes?: ShipmentBoxesType,
): ShipmentUserValueType => {
  const idKey = 'itemId';
  const itemKey = 'items';
  const skuKey = 'pSku';
  const variantKey = isPOShipment ? 'aliases' : 'variants';

  const boxes = allBoxes;

  let temp = {};

  shipment?.[itemKey]?.forEach(editedItems => {
    let item: ShipmentCreateItem | ShipmentDetailItemType | undefined;
    for (const origin of items) {
      item = origin.items.find(
        x =>
          [editedItems[idKey], editedItems.itemId].includes(x.itemId) &&
          x.originId === editedItems.originId,
      );
      if (item) break;
    }

    let qty = editedItems?.qty;
    if (CommonUtility.isValidObject(item)) {
      // only use 'available' when the property exists on the found item
      if ('available' in item && item.available < editedItems?.qty) {
        qty = (item as ShipmentCreateItem).available || 0;
      }
    }

    // since invoice will have no attributes we will try to get it from saved shipment

    const parsedEditedItem = {
      ...editedItems,
      _id: editedItems.itemId,
    };

    let itemToUse: any = parsedEditedItem;
    if (CommonUtility.isValidObject(item)) {
      itemToUse = {
        ...item,
        qty: (item as any).qty ?? editedItems?.qty ?? 0,
      };
    }
    if (shipment?.originType === ShipmentTypeKeys.WS && !isUnitView) {
      itemToUse = parsedEditedItem;
    }

    const latestAttribute = getShipmentLatestAttribute(
      itemToUse,
      shipment?.originType as any,
      true,
    );

    const tempValue = {
      grossWeight: latestAttribute?.cartonGrossWeight?.value,
      originId: itemToUse.originId,
      itemId: itemToUse.itemId,
      alias: latestAttribute?.alias,
      orgItem: itemToUse,
      supplierItemCost: itemToUse.supplierItemCost,
      sku: CommonUtility.isValidArray(editedItems?.marketplaceItemsSkuList)
        ? editedItems?.marketplaceItemsSkuList[0]
        : editedItems?.sku || item?.[skuKey],
      price: itemToUse.price,
      asin: itemToUse.secondaryIdentifier,
      qty: qty || 0,
      expirationDate: editedItems?.expirationDate
        ? new Date(editedItems?.expirationDate)
        : '',
      expanded: !!editedItems,
      QuantityInCase: latestAttribute?.unitsPerCarton
        ? (qty || 0) / latestAttribute?.unitsPerCarton
        : 0,
      ctnQty: latestAttribute?.unitsPerCarton
        ? CommonUtility.roundNumber(
            (qty || 0) / latestAttribute?.unitsPerCarton,
          )
        : 0,
    } as any;

    if (isUnitView) {
      boxes?.forEach(box => {
        const boxItem = box[itemKey].find(
          x =>
            x[idKey] === itemToUse.itemId && x.originId === itemToUse.originId,
        );

        if (boxItem) {
          tempValue[`box-${box._id}`] = boxItem.quantity;
        }
      });
    }

    const defaultOption = destinationType === 'awd' ? '' : 'SELLER';
    tempValue.labelOwner = editedItems?.labelOwner || defaultOption;
    tempValue.prepOwner = editedItems?.prepOwner || defaultOption;

    editedItems?.marketplaceItemsSkuList?.forEach(itemSku => {
      if (itemSku.toLowerCase() === item?.sku?.toLowerCase()) {
        tempValue.sku = itemSku;
      }
    });

    if (CommonUtility.isValidArray(editedItems?.aliases)) {
      editedItems.aliases.forEach(alias => {
        const relevantAlias = item?.[variantKey]?.find(
          x => x.alias === alias.alias,
        ) as ShipmentVariantType;
        let qty = alias.qty;
        if (relevantAlias?.available < alias.qty) {
          qty = relevantAlias.available;
        }
        if (relevantAlias) {
          if (isUnitView) {
            tempValue[alias.alias] = qty;
          } else {
            tempValue[alias.alias] =
              qty / (alias.attributes?.unitsPerCarton || 1);
          }
        }
      });
    }

    if (
      tempValue.ctnQty > (latestAttribute?.cartonQty || 0) &&
      CommonUtility.isValidObject(item)
    ) {
      // this is fallback Qty when ctnQty is not valid
      tempValue.oldQty = tempValue.ctnQty;
      tempValue.ctnQty = 0;
      tempValue.qty = 0;
    }

    if (!temp[editedItems.originId]) {
      temp[editedItems.originId] = {
        [editedItems.itemId]: tempValue,
      };
    } else {
      let itemObj = tempValue;
      if (temp[editedItems.originId][editedItems.itemId]) {
        itemObj = {
          ...temp[editedItems.originId][editedItems.itemId],
          ...tempValue,
        };
      }
      temp = {
        ...temp,
        [editedItems.originId]: {
          ...temp[editedItems.originId],
          [editedItems.itemId]: itemObj,
        },
      };
    }
  });

  return temp;
};

type originItemsType = {
  _id: string;
  items: ShipmentDetailItemType[];
}[];

export const sortDBItems = (
  items: ShipmentCreateItems | originItemsType,
  shipment: ShipmentDetailType,
  origins?: string[],
): ShipmentCreateItems => {
  let sortedItems = items.map(origin => {
    if (origins?.includes(origin._id)) {
      origin.items = origin.items.map(item => {
        const shipmentItem = shipment.items?.find(x =>
          [x.itemId].includes(item._id),
        );
        item.itemsUsedForShipment = shipmentItem?.qty || 0;
        return item;
      });
    }
    return origin;
  });

  sortedItems = sortedItems.map(origin => {
    origin.items = origin.items.sort((itemA, itemB) => {
      const editedItems = shipment.items?.find(x =>
        [x.itemId].includes(itemA._id),
      );
      const latestAttribute =
        getShipmentLatestAttribute(itemA, shipment?.originType as any, true) ||
        {};
      const ctn_qty = latestAttribute?.unitsPerCarton
        ? CommonUtility.roundNumber(
            (editedItems?.qty || 0) / latestAttribute?.unitsPerCarton,
          )
        : 0;

      const editedItems1 = shipment.items?.find(x => x.itemId === itemB._id);
      const latestAttribute1 =
        getShipmentLatestAttribute(itemB, shipment?.originType as any, true) ||
        {};
      const ctn_qty1 = latestAttribute1?.unitsPerCarton
        ? CommonUtility.roundNumber(
            (editedItems1?.qty || 0) / latestAttribute1?.unitsPerCarton,
          )
        : 0;
      return ctn_qty1 - ctn_qty;
    });
    return origin;
  });

  return sortedItems as ShipmentCreateItems;
};

type ShipmentItemAliasInput = {
  alias: string;
  qty: number;
  quantityInCase?: number;
  attributes?: {
    cartonLength: {
      unit: string;
      value: number;
    };
    cartonWidth: {
      unit: string;
      value: number;
    };
    cartonHeight: {
      unit: string;
      value: number;
    };
    cartonNetWeight: {
      unit: string;
      value: number;
    };
    cartonGrossWeight: {
      unit: string;
      value: number;
    };
    unitsPerCarton: number;
  };
};

type ShipmentItemInput = {
  itemId: string;
  originId: string;
  sku: string;
  upc?: string;
  asin?: string;
  qty: number;
  quantityInCase?: number;
  prepOwner?: string;
  labelOwner?: string;
  expirationDate?: string;
  aliases?: ShipmentItemAliasInput[];
};

export const prepareItems = (
  userValue: ShipmentUserValueType,
  attributeKey: string,
  aliasesKey: string,
  isUnitView: boolean,
  isPOShipment: boolean,
  type: string,
): ShipmentItemInput[] => {
  const items: ShipmentItemInput[] = [];
  Object.keys(userValue).forEach(key => {
    Object.values(userValue[key]).map(item => {
      const foundItem = item.orgItem;
      if (foundItem) {
        const temp: ShipmentItemInput = {
          itemId: item.itemId,
          originId: item.originId,
          sku: item.sku || foundItem.pSku,
          asin: foundItem.secondaryIdentifier || '',
          qty: 0,
          aliases: [],
        };

        if (item?.labelOwner) {
          temp.labelOwner = item.labelOwner;
        }

        if (item?.prepOwner) {
          temp.prepOwner = item.prepOwner;
        }

        if (item.expirationDate) {
          temp.expirationDate = item.expirationDate;
        }

        const isMultiple = isPOShipment
          ? foundItem.aliases?.length > 1
          : foundItem.variants?.filter(x => x.attribute)?.length > 1;

        if (isMultiple && !isUnitView) {
          foundItem[aliasesKey].forEach((variant: ShipmentVariantType) => {
            const ctn = Number(item[variant.alias] || 0);

            if (ctn) {
              const alias: ShipmentItemAliasInput = {
                attributes: {
                  cartonGrossWeight: variant[attributeKey]?.cartonGrossWeight,
                  cartonNetWeight: variant[attributeKey]?.cartonNetWeight,
                  cartonHeight: variant[attributeKey]?.cartonHeight,
                  cartonLength: variant[attributeKey]?.cartonLength,
                  cartonWidth: variant[attributeKey]?.cartonWidth,
                  unitsPerCarton: variant[attributeKey]?.unitsPerCarton,
                },
                alias: variant.alias,
                quantityInCase: ctn,
                qty: ctn * variant[attributeKey]?.unitsPerCarton,
              };

              temp.qty += alias.qty;
              temp.aliases?.push(alias);
            }
          });
        } else {
          const latestAttribute = getShipmentLatestAttribute(
            foundItem,
            type as any,
          );

          const ctn = Number(item.ctnQty || 0);
          const qty = Number(item.qty || 0);

          if (ctn || (isUnitView && qty)) {
            const alias: ShipmentItemAliasInput = {
              attributes: {
                cartonGrossWeight: latestAttribute?.cartonGrossWeight,
                cartonHeight: latestAttribute?.cartonHeight,
                cartonLength: latestAttribute?.cartonLength,
                cartonNetWeight: latestAttribute?.cartonNetWeight,
                cartonWidth: latestAttribute?.cartonWidth,
                unitsPerCarton: latestAttribute?.unitsPerCarton,
              },
              alias: latestAttribute?.alias,
              quantityInCase: ctn,
              qty,
            };

            temp.qty = alias.qty;
            if (!isUnitView) {
              temp.aliases?.push(alias);
            }
          }
        }

        if (temp.aliases?.length || (isUnitView && !!Number(item.qty))) {
          if (isUnitView) {
            delete temp.aliases;
          }
          items.push(temp);
        }
      }
    });
  });
  return items;
};

export const prepareBoxes = (
  boxesInformation: shipmentBoxInformationType[],
  userValue: ShipmentUserValueType,
): ShipmentBoxesType => {
  let boxes: ShipmentBoxesType = boxesInformation.map(box => {
    const copy: shipmentBoxInformationType = JSON.parse(JSON.stringify(box));
    delete copy._id;
    return {
      dimensions: {
        length: Number(copy.boxLength?.value ?? 0),
        width: Number(copy.boxWidth?.value ?? 0),
        height: Number(copy.boxHeight?.value ?? 0),
        unitOfMeasurement: copy.boxLength?.unit ?? '',
      },
      weight: {
        unit: copy.boxGrossWeight?.unit ?? '',
        value: Number(copy.boxGrossWeight?.value ?? 0),
      },
      items: [],
      _id: box._id,
    };
  });

  Object.keys(userValue).forEach(origin => {
    Object.values(userValue[origin]).forEach(userItem => {
      boxesInformation.forEach(box => {
        const boxId = `box-${box._id}`;
        if (userItem[boxId]) {
          const existingBoxIndex = boxes.findIndex(x => x._id === box._id);
          if (existingBoxIndex > -1) {
            boxes[existingBoxIndex].items.push({
              originId: origin,
              itemId: userItem.itemId,
              msku: userItem.sku || '',
              quantity: Number(userItem[boxId] || 0),
            });
          }
        }
      });
    });
  });

  boxes = boxes.map(box => {
    box.quantity = box.items.reduce(
      (sum, item) => sum + Number(item.quantity || 0),
      0,
    );
    delete box._id;
    return box;
  });

  return boxes;
};

export const cbmBox = box => {
  const cbm =
    ((box?.box_length?.value || 0) / 100) *
    ((box?.box_width?.value || 0) / 100) *
    ((box?.box_height?.value || 0) / 100);
  return cbm;
};

export const verifyBox = (boxes: ShipmentBoxesType = []) => {
  let valid = true;
  for (const box of boxes) {
    if (
      !(
        box?.dimensions?.height &&
        box?.dimensions?.length &&
        box?.weight?.value &&
        box?.dimensions?.width
      )
    ) {
      valid = false;
      break;
    }
  }
  return valid;
};

export const getUserValueAfterInputChange = (
  id,
  item,
  name,
  value,
  bulk = {},
  type,
  userValue,
  isComponent = false,
) => {
  const latestAttribute = getLatestAttribute(item, type, true);

  const tempValue = {
    // @ts-expect-error - TS18047: 'latestAttribute' is possibly 'null'.
    grossWeight: latestAttribute.carton_gross_weight?.value,
    origin_id: id,
    item_id: item._id,
    // @ts-expect-error - TS2339: Property 'alias' does not exist on type '{}'.
    alias: latestAttribute?.alias,
    asin: item.secondary_identifier,
    orgItem: item,
    ...bulk,
  };

  if (isComponent) {
    // @ts-expect-error - TS2339: Property 'unit_of_measurement' does not exist on type '{ grossWeight: any; origi...
    tempValue.unit_of_measurement = item.unit_of_measurement;
  }

  if (CommonUtility.isNotNull(name) && CommonUtility.isNotNull(value)) {
    tempValue[name] = value;
  }

  if (name === 'ctnQty') {
    // @ts-expect-error - TS2339: Property 'qty' does not exist on type '{ grossWeight: any; origin_id: any; item_...
    tempValue.qty = value * latestAttribute?.units_per_carton;
    // @ts-expect-error - TS2339: Property 'QuantityInCase' does not exist on type '{ grossWeight: any; origin_id:...
    tempValue.QuantityInCase = value;
    // @ts-expect-error - TS2339: Property 'expanded' does not exist on type '{ grossWeight: any; origin_id: any; ...
    tempValue.expanded = true;
  } else if (name === 'qty') {
    // @ts-expect-error - TS2339: Property 'expanded' does not exist on type '{ grossWeight: any; origin_id: any; ...
    tempValue.expanded = true;
  }

  if (value) {
    // @ts-expect-error - TS2339: Property 'old_qty' does not exist on type '{ grossWeight: any; origin_id: any; i...
    tempValue.old_qty = '';
  }

  let temp = { ...userValue };
  if (!temp[id]) {
    temp[id] = {
      [item._id]: tempValue,
    };
  } else {
    let itemObj = tempValue;
    if (temp[id][item._id]) {
      itemObj = {
        ...temp[id][item._id],
        ...tempValue,
      };
    }
    temp = {
      ...temp,
      [id]: {
        ...temp[id],
        [item._id]: itemObj,
      },
    };
  }

  return {
    userValue: temp,
    changed: name && !['error', 'expanded'].includes(name),
  };
};

const fixField = fieldValue => {
  if (CommonUtility.isValidDate(fieldValue)) {
    return DateUtility.dateToServerDate(fieldValue);
  }
  return fieldValue;
};

export const prepareDFvalues = values => {
  const dfValues = JSON.parse(JSON.stringify(values));

  const checkFields = fields => {
    if (fields === null || fields === undefined) {
      return fields;
    }

    if (Array.isArray(fields)) {
      for (let index = 0; index < fields.length; index += 1) {
        fields[index] = checkFields(fields[index]);
      }
    } else if (
      typeof fields === 'object' &&
      !CommonUtility.isValidDate(fields)
    ) {
      for (const key of Object.keys(fields)) {
        fields[key] = checkFields(fields[key]);
      }
    } else {
      fields = fixField(fields);
    }
    return fields;
  };

  const dynamicFieldValues = checkFields(dfValues);
  return dynamicFieldValues;
};

export const populatePOforUpdating = (data, currentPOItem, updatedItem) => {
  const toSend = { ...data };
  toSend.items = toSend.items.map(item => {
    const mainItem = {};
    if (!currentPOItem.component) {
      if (item._id === updatedItem.item_id && item.aliases?.length) {
        item.aliases = item.aliases.map(aliasItem => {
          if (aliasItem.alias === updatedItem.attributes?.[0]?.alias) {
            aliasItem.attributes = updatedItem.attributes?.[0];
            delete aliasItem.attributes.alias;
          }
          return aliasItem;
        });
      }
    }
    if (item.aliases?.length) {
      item.aliases = item.aliases.map(aliasItem => {
        delete aliasItem._id;
        return aliasItem;
      });
    }
    // @ts-expect-error - TS2339: Property 'aliases' does not exist on type '{}'.
    mainItem.aliases = item.aliases;
    // @ts-expect-error - TS2339: Property 'prices' does not exist on type '{}'.
    mainItem.prices = item.prices;
    // @ts-expect-error - TS2339: Property 'item_upc' does not exist on type '{}'.
    mainItem.item_upc = item.item_upc;
    return mainItem;
  });

  toSend.components = toSend.components.map(item => {
    const mainItem = {};
    if (currentPOItem.component) {
      if (item.component_id === updatedItem.item_id && item.aliases?.length) {
        item.aliases = item.aliases.map(aliasItem => {
          if (aliasItem.alias === updatedItem.attributes?.[0]?.alias) {
            aliasItem.attributes = updatedItem.attributes?.[0];
            delete aliasItem.attributes.alias;
          }
          return aliasItem;
        });
      }
    }
    if (item.aliases?.length) {
      item.aliases = item.aliases.map(aliasItem => {
        delete aliasItem._id;
        return aliasItem;
      });
    }
    // @ts-expect-error - TS2339: Property 'aliases' does not exist on type '{}'.
    mainItem.aliases = item.aliases;
    // @ts-expect-error - TS2339: Property 'prices' does not exist on type '{}'.
    mainItem.prices = item.prices;
    // @ts-expect-error - TS2339: Property 'component_id' does not exist on type '{}'.
    mainItem.component_id = item.component_id;
    return mainItem;
  });
  const bodyData = {
    down_payment: toSend.down_payment,
    upon_shipment: toSend.upon_shipment,
    lead_time: toSend.lead_time,
    price_term: toSend.price_term,
    notify: toSend.notify,
    send_pdf: toSend.send_pdf,
    email_addresses: toSend.email_addresses,
    manufacturer_reference_number: toSend.manufacturer_reference_number,
    items: toSend.items,
    components: toSend.components,
    custom_items: [
      ...toSend.custom_items.map(x => {
        const t = { ...x };
        delete t._id;
        return t;
      }),
    ],
  };
  return bodyData;
};

function PlaceholderLines({ count }: { count: number }) {
  return (
    <div className="sk:flex sk:flex-col sk:gap-2.5">
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          className="sk:h-2.5 sk:animate-pulse sk:rounded-xs sk:bg-fill-secondary"
        />
      ))}
    </div>
  );
}

function LoadingTabCard() {
  return (
    <ShadowBox className="sk:min-w-0 sk:flex-1 sk:px-4">
      <div className="sk:flex sk:flex-col sk:gap-5" aria-hidden>
        <PlaceholderLines count={5} />
        <PlaceholderLines count={3} />
      </div>
    </ShadowBox>
  );
}

export function LoadingTabs() {
  return (
    <div className="sk:flex sk:flex-wrap sk:-mx-4 sk:px-3.5! sk:gap-[15px]">
      <LoadingTabCard />
      <LoadingTabCard />
    </div>
  );
}

export const fbaShipmentSteps = {
  transportOptions: 'TRANSPORT_OPTIONS',
};
